<script setup>
import useVuelidate from '@vuelidate/core'
import { integer, maxLength, minLength, required } from '@vuelidate/validators'
import {computed} from 'vue'

import DropdownCountry from '@/components/form/DropdownCountry.vue'
import InputText from '@/components/form/InputText.vue'
import { useAddressStore } from '@/stores/AddressStore'
import { useValidators } from '@/useValidators'

const { phoneValidator } = useValidators()
const addressStore = useAddressStore()

const props = defineProps({
  submitLabel: {
    type: String,
    required: true
  },
  modelValue: {
    type: Object,
    default: () => {
      return {
        id: null,
        first_name: '',
        last_name: '',
        address: '',
        number: '',
        zip: '',
        city: '',
        country: window.settings.version,
        phone_number: '',
        company_name: '',
        company_id: '',
        tax_id: ''
      }
    }
  },
  isSubmitHidden: {
    type: Boolean,
    default: false
  }
})

const validationRules = computed(() => {
  return {
    first_name: { required, maxLength: maxLength(50) },
    last_name: { required, maxLength: maxLength(50) },
    phone_number: {
      required,
      phone: phoneValidator
    },
    address: { required, maxLength: maxLength(50) },
    number: { required, maxLength: maxLength(10) },
    zip: {
      required,
      integer,
      minLength: minLength(5),
      maxLength: maxLength(5)
    },
    country: { required },
    city: { required },
    company_name: {},
  }
})

const address = computed({
  get() {
    return props.modelValue
  },
  set(newAddress) {
    emits('update:modelValue', newAddress)
  }
})

const $vA = useVuelidate(validationRules, address, { $stopPropagation: true })

const emits = defineEmits(['update:modelValue', 'onSuccessfulUpdate'])

const handleUpdate = () => {
  $vA.value.$touch()

  if (!$vA.value.$invalid) {
    emits('onSuccessfulUpdate')
  }
}

const isValid = () => {
  $vA.value.$touch()
  return !$vA.value.$invalid
}

defineExpose({
  isValid
})
</script>

<template>
  <div class="step-address__form">
    <p v-if="!addressStore.hasAddress" class="step-content__description">
      {{
        $t(
          'You do not have any address saved yet. Please add your delivery address for the magazine.'
        )
      }}
    </p>

    <InputText
      v-model="$vA.first_name.$model"
      autocomplete="given-name"
      name="first_name"
      :validator="$vA.first_name"
      :label="$t('First name')"
      :placeholder="$t('First name placeholder')"
    />

    <InputText
      v-model="$vA.last_name.$model"
      autocomplete="family-name"
      name="last_name"
      :validator="$vA.last_name"
      :label="$t('Last name')"
      :placeholder="$t('Last name placeholder')"
    />

    <InputText
      v-model="$vA.company_name.$model"
      name="organization"
      autocomplete="organization"
      :validator="$vA.company_name"
      :label="$t('Company name (optional)')"
      :placeholder="$t('Company name placeholder')"
    />

    <InputText
      v-model="$vA.phone_number.$model"
      name="phone_number"
      autocomplete="tel"
      type="tel"
      :validator="$vA.phone_number"
      :label="$t('Phone number (for delivery)')"
      :placeholder="$t('Phone number placeholder')"
    />

    <InputText
      v-model="$vA.address.$model"
      name="address"
      autocomplete="address-line1"
      :validator="$vA.address"
      :label="$t('Street')"
      :placeholder="$t('Street placeholder')"
    />

    <InputText
      v-model="$vA.number.$model"
      name="number"
      autocomplete="address-line2"
      :validator="$vA.number"
      :label="$t('Street number')"
      :placeholder="$t('Street number placeholder')"
    />

    <InputText
      v-model="$vA.city.$model"
      name="city"
      autocomplete="address-level2"
      :validator="$vA.city"
      :label="$t('City')"
      :placeholder="$t('City placeholder')"
    />

    <InputText
      v-model="$vA.zip.$model"
      name="zip"
      autocomplete="postal-code"
      :validator="$vA.zip"
      :label="$t('ZIP code')"
      :placeholder="$t('ZIP code placeholder')"
    />

    <DropdownCountry v-model="$vA.country.$model"/>

    <div v-if="!isSubmitHidden" class="input-group button-group">
      <button
        @click="handleUpdate"
        class="button button--large button--primary step-address__button"
      >
        {{ submitLabel }}
      </button>
    </div>
  </div>
</template>
